/**
 * @file CtaBar.tsx
 * @description Full-width call-to-action banner. Fetches card entries by UID server-side so the
 * client receives fully-resolved data. Card selection is delegated to CtaBarRandomCard (the only
 * 'use client' boundary) so this component stays a server component.
 */

import { Container } from '@/components/primitives/Container/Container';
import { toPascalCase } from '@/utils/string-utils';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { ICtaBar, ICtaBarCards } from '@/.generated';
import { getEntriesByUids } from '@/lib/contentstack/entries';
import { CtaBarRandomCard } from './CtaBarRandomCard';

type CtaBarProps = WithMetadata<IBaseComponentProps> & ICtaBar;

// Async server component — fetches full card entries then hands them to the client picker
const Default = async (props: CtaBarProps) => {
  const { componentName, _metadata, inline_navigation_id, enable_side_margins, hide_component, remove_bottom_spacing } =
    props;

  if (hide_component) return null;

  // Extract UIDs from the CMS reference stubs; filter out any undefined values
  const entryUids =
    props.reference?.map((ref) => ref.uid).filter((uid): uid is string => Boolean(uid)) ?? [];

  // Nothing to render when no cards are referenced
  if (!entryUids.length) return null;

  // Fetch fully-resolved card entries by their UIDs
  const result = await getEntriesByUids<ICtaBarCards>({
    contentTypeUid: 'cta_bar_cards',
    entryUids,
    filterBySite: false,
    referencesToInclude: [
      'standard_content_page',
      'filter_page',
      'non_customer_page',
      'pricing_page',
      'rebates_page',
      'topic_sub_navigation_hub_page',
      'demand_calculator_page',
      'learning_materials_page',
      'agenda_list_page',
      'home_page',
    ],
  });

  const cards = (result?.entries ?? []) as ICtaBarCards[];

  // Guard against an empty or failed fetch
  if (!cards.length) return null;

  return (
    <Container
      componentName={componentName}
      id={inline_navigation_id}
      metaId={_metadata?.uid}
      containerBleed
      className={enable_side_margins ? 'px-0' : 'px-0 md:px-0'}
      bottomMargin={!remove_bottom_spacing}
    >
      <CtaBarRandomCard cards={cards} />
    </Container>
  );
};

const variants = {
  Default,
};

// CtaBar entry point — resolves the correct variant from `component_variant` (defaults to "Default")
export const CtaBar = (props: CtaBarProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
